import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { ProjectMethodologyEnum } from 'gql/graphql';

import { useMemo } from 'react';

import { getLimitedCostTypesGql } from '../../CatalogLimitedType/CatalogLimitedTypeQueries';
import { otherCostTypesGql } from '../../CatalogOthersType/CatalogOthersTypeQueries';
import { globalLimitedCostCandidateTypesGql, globalOtherCostCandidateTypesGql } from '../catalogOtherAndLimitedQueries';
import type { TCatalogCostTypeOption } from '../types';
import { CatalogOtherAndLimitedTab } from '../types';

export const useCatalogCostCandidateTypes = ({
    tab,
    methodology,
    excludeId,
}: {
    tab: CatalogOtherAndLimitedTab;
    methodology: ProjectMethodologyEnum;
    excludeId?: number;
}) => {
    const isOthersTab = tab === CatalogOtherAndLimitedTab.Others;

    const otherVariables = useMemo(
        () => ({
            methodology,
            excludeGlobalOtherCostId: excludeId,
        }),
        [excludeId, methodology],
    );
    const limitedVariables = useMemo(
        () => ({
            methodology,
            excludeGlobalLimitedCostId: excludeId,
        }),
        [excludeId, methodology],
    );

    const [otherTypesData, otherTypesStatus] = useRequest({
        requestId: 'globalOtherCostCandidateTypes',
        query: globalOtherCostCandidateTypesGql,
        variables: otherVariables,
        withEffect: isOthersTab,
    });
    const [limitedTypesData, limitedTypesStatus] = useRequest({
        requestId: 'globalLimitedCostCandidateTypes',
        query: globalLimitedCostCandidateTypesGql,
        variables: limitedVariables,
        withEffect: !isOthersTab,
    });

    const typeCatalogVariables = useMemo(() => ({ methodology }), [methodology]);
    const [otherTypeCatalogData] = useRequest({
        requestId: 'catalogOtherCostTypesForSystemLock',
        query: otherCostTypesGql,
        variables: typeCatalogVariables,
        withEffect: isOthersTab,
    });
    const [limitedTypeCatalogData] = useRequest({
        requestId: 'catalogLimitedCostTypesForSystemLock',
        query: getLimitedCostTypesGql,
        variables: typeCatalogVariables,
        withEffect: !isOthersTab,
    });

    const systemTypeIds = useMemo(() => {
        const catalogTypes: Array<{ uniqueId: string; isSystem: boolean }> = isOthersTab
            ? otherTypeCatalogData?.otherCostTypes ?? []
            : limitedTypeCatalogData?.limitedCostTypes ?? [];

        return new Set(catalogTypes.filter((item) => item.isSystem).map((item) => item.uniqueId));
    }, [isOthersTab, limitedTypeCatalogData?.limitedCostTypes, otherTypeCatalogData?.otherCostTypes]);

    const items = useMemo<TCatalogCostTypeOption[]>(() => {
        const sourceItems = isOthersTab
            ? otherTypesData?.globalOtherCostCandidateTypes ?? []
            : limitedTypesData?.globalLimitedCostCandidateTypes ?? [];

        return sourceItems.map((item) => ({
            id: item.uniqueId,
            name: item.name,
            isSystem: item.isSystem || systemTypeIds.has(item.uniqueId),
        }));
    }, [
        isOthersTab,
        limitedTypesData?.globalLimitedCostCandidateTypes,
        otherTypesData?.globalOtherCostCandidateTypes,
        systemTypeIds,
    ]);

    const isLoading = isOthersTab
        ? otherTypesStatus === RequestStatus.FETCHING
        : limitedTypesStatus === RequestStatus.FETCHING;

    return {
        items,
        isLoading,
        systemTypeIds,
    };
};
